import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../AuthContext';
import { REPORT_TYPES } from '../../constants/reportTypes';
import { 
    generateReport, 
    getReportHistory, 
    getUserSettings,
    spendCredits as apiSpendCredits,
    getUserCredits
} from '../../api';
import ReportHistory from './ReportHistory';
import ReportViewer from './ReportViewer';
import ReportTypeList from './ReportTypeList';
import MessagePopup from '../utils/MessagePopup';

// ═══════════════════════════════════════════════════════════════════════════════
// ✅ CONSTANTS - REUSABLE CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════

const PROGRESS_CONFIG = {
    INTERVAL_MS: 3000,
    INCREMENT: 10,
    MAX_PROGRESS: 90,
    COMPLETE: 100,
    RESET_DELAY_MS: 2000
};

const DEFAULT_VALUES = {
    LANGUAGE: 'ENGLISH',
    TIMEZONE: 'Asia/Kolkata',
    PLACE: 'Unknown',
    RELIGION: 'Hindu',
    LATITUDE: 0,
    LONGITUDE: 0
};

const STORAGE_KEYS = {
    D9_CHART: (userId) => `astroguru_d9chart_${userId}`,
    USER: 'astroguru_user'
};

const MESSAGE_TEMPLATES = {
    INSUFFICIENT_CREDITS: (required, available) => 
        ` Insufficient credits! You need ${required} credits but have only ${available}. Please purchase more credits.`,
    CACHED_REPORT: '📜 Report loaded from history! No credits deducted.',
    REPORT_GENERATED: (reportName, credits, newBalance) =>
        `✅ ${reportName} generated successfully! ${credits} credits spent. New balance: ${newBalance}`,
    CREDIT_DEDUCTION_FAILED: (error) =>
        `⚠️ Report generated but credits not deducted: ${error}. Please contact support.`,
    REPORT_FAILED: (error) => ` ${error}`,
    NO_SPEND_TYPE: '⚠️ Report generated but could not deduct credits. Contact support.',
    USER_NOT_LOADED: 'User data not loaded. Please refresh the page.',
    HISTORY_FAILED: ' Failed to load report from history'
};

// ═══════════════════════════════════════════════════════════════════════════════
// ✅ HELPER FUNCTIONS - EXTRACTED FOR REUSABILITY
// ═══════════════════════════════════════════════════════════════════════════════

const formatDateTime = (date, time) => {
    let formattedDate = date;
    let formattedTime = time;

    // Format date
    if (date instanceof Date) {
        formattedDate = date.toISOString().split('T')[0];
    } else if (typeof date === 'string' && date.includes('T')) {
        formattedDate = date.split('T')[0];
    }

    // Format time
    if (time instanceof Date) {
        const hours = String(time.getUTCHours()).padStart(2, '0');
        const minutes = String(time.getUTCMinutes()).padStart(2, '0');
        const seconds = String(time.getUTCSeconds()).padStart(2, '0');
        formattedTime = `${hours}:${minutes}:${seconds}`;
    } else if (typeof time === 'string' && time.includes('T')) {
        formattedTime = time.split('T')[1].split('.')[0];
    }

    return { date: formattedDate, time: formattedTime };
};

// ═══════════════════════════════════════════════════════════════════════════════
// ✅ FIXED D9 Chart Transformation Helper with FULL LOGGING
// ═══════════════════════════════════════════════════════════════════════════════

const transformD9ChartData = (d9Chart) => {
  console.log('\n🔄 ========== D9 CHART TRANSFORMATION START ==========');
  
  if (!d9Chart) {
    console.warn('⚠️ No D9 chart provided to transform');
    console.log('🔄 ========== D9 CHART TRANSFORMATION END (NULL) ==========\n');
    return null;
  }

  // ✅ AuthContext returns data directly, not nested in .data property
  const d9Data = d9Chart;
  
  console.log('📊 D9 Raw Data:', {
    chartId: d9Data.d9ChartsId,
    source: d9Data.source,
    ayanamsa: d9Data.ayanamsa,
    hasGrahaTable: !!d9Data.graha_table,
    grahaTableType: typeof d9Data.graha_table,
    isArray: Array.isArray(d9Data.graha_table),
    grahaCount: Array.isArray(d9Data.graha_table) ? d9Data.graha_table.length : 0
  });

  // ✅ Check if graha_table exists and is an array
  if (!d9Data.graha_table || !Array.isArray(d9Data.graha_table)) {
    console.error(' D9 chart missing graha_table array');
    console.log('🔄 ========== D9 CHART TRANSFORMATION END (INVALID) ==========\n');
    return null;
  }

  // ✅ Transform graha_table array to required format
  const grahaTable = d9Data.graha_table.map((planet, index) => {
    const transformed = {
      planet: planet.graha || planet.grahaSymbol || `Planet_${index + 1}`,
      graha: planet.graha || planet.grahaSymbol || '',
      grahaSymbol: planet.grahaSymbol || planet.graha || '',
      longitude: planet.longitude || '',
      nakshatra: planet.nakshatra?.split(' ')[0] || '',
      nakshatrapada: parseInt(planet.nakshatra?.split(' ')[1]) || 0,
      lordSubLord: planet.lordSubLord || '',
      rulerof: planet.rulerOf === '-' ? [] : (planet.rulerOf || '').split(', '),
      isIn: parseInt(planet.isIn) || 0,
      bhavaOwner: planet.bhavaOwner || '',
      relationship: planet.relationship || '',
      dignities: planet.dignities || '',
      isretrograde: planet.graha?.includes('↺') || false
    };

    console.log(`  ✅ Transformed ${transformed.planet}:`, {
      house: transformed.isIn,
      nakshatra: transformed.nakshatra
    });

    return transformed;
  });

  const transformed = {
    charttype: d9Data.chart_type || 'D9 (Navamsha)',
    ayanamsa: d9Data.ayanamsa,
    grahatable: grahaTable
  };

  console.log('✅ D9 Transformed Successfully:', {
    chartType: transformed.charttype,
    grahaCount: transformed.grahatable.length,
    planets: transformed.grahatable.map(g => g.planet)
  });
  console.log('🔄 ========== D9 CHART TRANSFORMATION END ==========\n');

  return transformed;
};

// ═══════════════════════════════════════════════════════════════════════════════
// ✅ MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const ReportsPage = () => {
    console.log('\n🏗️ ========== REPORTS PAGE RENDER ==========');
    
    // ✅ Auth Context
    const { 
        user, 
        setUser, 
        refreshCredits, 
        d1ChartData,
        d1ChartLoading,
        loadD1Chart,
        d9ChartData: authD9ChartData,
        d9ChartLoading,
        loadD9Chart
    } = useAuth();
    
    // ✅ State Management
    const [selectedReportType, setSelectedReportType] = useState(null);
    const [loading, setLoading] = useState(false);
    const [reportContent, setReportContent] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [error, setError] = useState(null);
    const [reportHistory, setReportHistory] = useState([]);
    const [selectedLanguage, setSelectedLanguage] = useState(DEFAULT_VALUES.LANGUAGE);
    const [userSettings, setUserSettings] = useState(null);
    const [showConfirmPopup, setShowConfirmPopup] = useState(false);
    const [pendingReportType, setPendingReportType] = useState(null);
    const [generationProgress, setGenerationProgress] = useState(0);
    const [showMessagePopup, setShowMessagePopup] = useState(false);
    const [popupMessage, setPopupMessage] = useState('');

    // ✅ Failure Tracking Refs
    const d1LoadAttempted = useRef(false);
    const d9LoadAttempted = useRef(false);
    const d1LoadFailed = useRef(false);
    const d9LoadFailed = useRef(false);

    console.log('👤 User State:', {
        userId: user?.userId,
        fullName: user?.full_name,
        hasD1Chart: !!d1ChartData,
        hasD9Chart: !!authD9ChartData,
        language: selectedLanguage,
        credits: user?.credits
    });

    // ═══════════════════════════════════════════════════════════════════════════════
    // ✅ LAZY LOAD D1 CHART (ONLY WHEN NEEDED, ONLY ONCE)
    // ═══════════════════════════════════════════════════════════════════════════════
    
    const ensureD1Chart = useCallback(async () => {
        console.log('\n📊 ========== D1 CHART ENSURE START ==========');
        
        if (d1ChartData) {
            console.log('✅ Using D1 chart from AuthContext:', {
                grahaCount: d1ChartData.graha_table?.length,
                bhavaCount: d1ChartData.bhava_table?.length,
                chartType: d1ChartData.chart_type
            });
            console.log('📊 ========== D1 CHART ENSURE END (CACHED) ==========\n');
            return d1ChartData;
        }

        if (d1LoadFailed.current) {
            console.log('⚠️ D1 chart load previously failed, skipping retry');
            console.log('📊 ========== D1 CHART ENSURE END (FAILED) ==========\n');
            return null;
        }

        if (d1LoadAttempted.current && d1ChartLoading) {
            console.log('⏳ D1 chart is already loading...');
            console.log('📊 ========== D1 CHART ENSURE END (LOADING) ==========\n');
            return null;
        }

        d1LoadAttempted.current = true;

        try {
            console.log('🔄 Lazy loading D1 chart...');
            const chart = await loadD1Chart();
            
            if (!chart) {
                d1LoadFailed.current = true;
                console.warn('⚠️ D1 chart load returned null');
            } else {
                console.log('✅ D1 chart loaded successfully:', {
                    grahaCount: chart.graha_table?.length,
                    bhavaCount: chart.bhava_table?.length
                });
            }
            
            console.log('📊 ========== D1 CHART ENSURE END ==========\n');
            return chart;
        } catch (error) {
            d1LoadFailed.current = true;
            console.error(' D1 chart load failed:', error.message);
            console.log('📊 ========== D1 CHART ENSURE END (ERROR) ==========\n');
            return null;
        }
    }, [d1ChartData, d1ChartLoading, loadD1Chart]);

    // ═══════════════════════════════════════════════════════════════════════════════
    // ✅ LAZY LOAD D9 CHART (ONLY WHEN NEEDED, ONLY ONCE)
    // ═══════════════════════════════════════════════════════════════════════════════

    const ensureD9Chart = useCallback(async () => {
      console.log('\n📊 ========== D9 CHART ENSURE START (REPORTS PAGE) ==========');

      // ✅ FIRST: Check AuthContext
      if (authD9ChartData) {
        console.log('✅ Using D9 chart from AuthContext:', {
          chartId: authD9ChartData.d9ChartsId,
          source: authD9ChartData.source,
          hasGrahaTable: !!authD9ChartData.graha_table,
          grahaTableType: typeof authD9ChartData.graha_table,
          isArray: Array.isArray(authD9ChartData.graha_table),
          grahaCount: authD9ChartData.graha_table?.length || 0
        });

        // ✅ Check if graha_table exists and is valid
        if (authD9ChartData.graha_table && Array.isArray(authD9ChartData.graha_table)) {
          console.log(`✅ D9 graha_table is valid array: ${authD9ChartData.graha_table.length} planets`);
          console.log('📊 ========== D9 CHART ENSURE END (CACHED) ==========\n');
          return authD9ChartData;  // ✅ Return the chart directly, NOT .data
        } else {
          console.warn('⚠️ D9 chart has invalid graha_table structure');
        }
      }

      // ✅ Check localStorage as fallback
      if (user?.userId) {
        const storageKey = `astroguru_d9chart_${user.userId}`;
        const storedD9 = localStorage.getItem(storageKey);
        
        if (storedD9) {
          try {
            const parsed = JSON.parse(storedD9);
            console.log('📦 Found D9 chart in localStorage:', {
              chartId: parsed.d9ChartsId,
              source: parsed.source,
              hasGrahaTable: !!parsed.graha_table,
              grahaCount: parsed.graha_table?.length || 0
            });

            if (parsed.graha_table && Array.isArray(parsed.graha_table)) {
              console.log('📊 ========== D9 CHART ENSURE END (LOCALSTORAGE) ==========\n');
              return parsed;
            }
          } catch (e) {
            console.error(' Failed to parse D9 from localStorage:', e);
          }
        }
      }

      // ✅ Load from backend if not in cache
      if (d9LoadFailed.current) {
        console.log('⏸️ D9 chart load previously failed, skipping retry');
        console.log('📊 ========== D9 CHART ENSURE END (FAILED) ==========\n');
        return null;
      }

      if (d9LoadAttempted.current || d9ChartLoading) {
        console.log('⏸️ D9 chart is already loading...');
        console.log('📊 ========== D9 CHART ENSURE END (LOADING) ==========\n');
        return null;
      }

      if (!user?.birth_date || !user?.birth_time) {
        console.log('⚠️ Cannot load D9 chart: Missing birth date or time', {
          hasBirthDate: !!user?.birth_date,
          hasBirthTime: !!user?.birth_time
        });
        d9LoadFailed.current = true;
        console.log('📊 ========== D9 CHART ENSURE END (NO DATA) ==========\n');
        return null;
      }

      d9LoadAttempted.current = true;

      try {
        console.log('🔄 Lazy loading D9 chart from AuthContext...');
        const chart = await loadD9Chart(true); // FORCE REFRESH

        if (!chart) {
          d9LoadFailed.current = true;
          console.warn('⚠️ D9 chart load returned null');
        } else {
          console.log('✅ D9 chart loaded successfully:', {
            chartId: chart.d9ChartsId,
            source: chart.source,
            hasGrahaTable: !!chart.graha_table,
            grahaCount: chart.graha_table?.length || 0
          });
        }

        console.log('📊 ========== D9 CHART ENSURE END ==========\n');
        return chart;
      } catch (error) {
        d9LoadFailed.current = true;
        console.error(' D9 chart load failed:', error.message);
        console.log('📊 ========== D9 CHART ENSURE END (ERROR) ==========\n');
        return null;
      }
    }, [authD9ChartData, d9ChartLoading, loadD9Chart, user]);

    // ═══════════════════════════════════════════════════════════════════════════════
    // ✅ BUILD BIRTH PROFILE
    // ═══════════════════════════════════════════════════════════════════════════════
    
    const buildBirthProfile = useCallback(() => {
        console.log('\n👤 ========== BUILD BIRTH PROFILE START ==========');
        
        const profile = {
            name: user?.full_name || user?.given_name || user?.name || 'User',
            fullname: user?.full_name || user?.name || 'User',
            dob: user?.birth_date || 'unknown',
            birthdate: user?.birth_date,
            time: user?.birth_time || 'unknown',
            birthtime: user?.birth_time,
            timezone: user?.timezone || DEFAULT_VALUES.TIMEZONE,
            place: user?.birth_place || DEFAULT_VALUES.PLACE,
            birthplace: user?.birth_place,
            location: user?.birth_place,
            latLong: user?.latitude && user?.longitude 
                ? `${user.latitude}, ${user.longitude}` 
                : 'unknown',
            latitude: user?.latitude || DEFAULT_VALUES.LATITUDE,
            longitude: user?.longitude || DEFAULT_VALUES.LONGITUDE,
            religion: user?.religion || DEFAULT_VALUES.RELIGION,
            hasD1Chart: !!d1ChartData,
            hasD9Chart: !!authD9ChartData,
            isTempProfile: false
        };

        console.log('✅ Birth Profile Built:', profile);
        console.log('👤 ========== BUILD BIRTH PROFILE END ==========\n');

        return profile;
    }, [user, d1ChartData, authD9ChartData]);

    // ═══════════════════════════════════════════════════════════════════════════════
    // ✅ FETCH FUNCTIONS
    // ═══════════════════════════════════════════════════════════════════════════════
    
    const fetchReportHistory = useCallback(async () => {
        if (!user?.userId) {
            console.log('⚠️ No userId available for fetching report history');
            return;
        }

        try {
            console.log('📤 Fetching report history for userId:', user.userId);
            const response = await getReportHistory(user.userId);
            
            if (response.data.success) {
                setReportHistory(response.data.reports);
                console.log('✅ Fetched report history:', response.data.reports.length, 'reports');
            }
        } catch (error) {
            console.error(' Error fetching report history:', error);
            
            if (error.response?.status === 401) {
                console.warn('⚠️ 401 error while fetching history - token may be expired');
            }
        }
    }, [user?.userId]);

    const fetchUserSettings = useCallback(async () => {
        if (!user?.userId) {
            setSelectedLanguage(DEFAULT_VALUES.LANGUAGE);
            return;
        }

        try {
            const response = await getUserSettings(user.userId);
            if (response.data) {
                setUserSettings(response.data);
                setSelectedLanguage(response.data.Language || DEFAULT_VALUES.LANGUAGE);
                console.log('✅ User language loaded:', response.data.Language);
            } else {
                setSelectedLanguage(DEFAULT_VALUES.LANGUAGE);
            }
        } catch (err) {
            console.error(' Failed to fetch settings:', err);
            setSelectedLanguage(DEFAULT_VALUES.LANGUAGE);
        }
    }, [user]);

    const refreshUserCredits = useCallback(async () => {
        if (!user?.userId) return;

        try {
            console.log('🔄 Refreshing user credits...');
            
            if (refreshCredits) {
                await refreshCredits();
                console.log('✅ Credits refreshed via AuthContext');
                return;
            }
            
            const response = await getUserCredits(user.userId);
            
            if (response.data.success) {
                const newBalance = response.data.balance.currentCredits;
                console.log('✅ Credits refreshed:', newBalance);
                
                setUser(prev => ({
                    ...prev,
                    credits: newBalance
                }));
            }
        } catch (error) {
            console.error(' Failed to refresh credits:', error);
        }
    }, [user?.userId, setUser, refreshCredits]);

    // ═══════════════════════════════════════════════════════════════════════════════
    // ✅ CREDIT HANDLING
    // ═══════════════════════════════════════════════════════════════════════════════
    
    const handleSpendCredits = useCallback(async (spendTypeId, reportName) => {
        if (!user?.userId) {
            console.error(' No userId available for credit spending');
            return { success: false, error: 'User not logged in' };
        }

        try {
            console.log('💳 Spending credits:', {
                userId: user.userId,
                spendTypeId,
                description: `Report: ${reportName}`
            });

            const response = await apiSpendCredits(
                user.userId,
                spendTypeId,
                `Report: ${reportName}`
            );

            if (response.data.success) {
                console.log('✅ Credits spent successfully');
                
                const newBalance = response.data.balance.currentCredits;
                
                const updatedUser = {
                    ...user,
                    credits: newBalance
                };
                
                setUser(updatedUser);
                
                const existingUserData = JSON.parse(localStorage.getItem(STORAGE_KEYS.USER) || '{}');
                localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify({
                    ...existingUserData,
                    credits: newBalance
                }));
                
                console.log('💰 New balance:', newBalance);

                return { 
                    success: true, 
                    newBalance: newBalance,
                    spendId: response.data.spend.spendId
                };
            } else {
                throw new Error(response.data.error || 'Failed to spend credits');
            }
        } catch (error) {
            console.error(' Error spending credits:', error);
            
            if (error.response?.status === 400) {
                await refreshUserCredits();
            }
            
            return { 
                success: false, 
                error: error.response?.data?.error || error.response?.data?.message || error.message 
            };
        }
    }, [user, setUser, refreshUserCredits]);

    // ═══════════════════════════════════════════════════════════════════════════════
    // ✅ EFFECTS
    // ═══════════════════════════════════════════════════════════════════════════════
    
    useEffect(() => {
        if (user?.userId) {
            fetchUserSettings();
        }
    }, [user?.userId, fetchUserSettings]);

    useEffect(() => {
        if (user?.userId) {
            fetchReportHistory();
        }
    }, [user?.userId, fetchReportHistory]);

    // ═══════════════════════════════════════════════════════════════════════════════
    // ✅ REPORT GENERATION HANDLERS
    // ═══════════════════════════════════════════════════════════════════════════════
    
    const handleReportTypeClick = useCallback((reportType) => {
        console.log('📋 Report type selected:', reportType.id);

        if (!user?.userId) {
            setPopupMessage(MESSAGE_TEMPLATES.USER_NOT_LOADED);
            setShowMessagePopup(true);
            return;
        }

        setPendingReportType(reportType);
        setShowConfirmPopup(true);
    }, [user]);

    const handleConfirmGeneration = useCallback(async () => {
        if (!pendingReportType) return;

        console.log('\n🚀 ========== REPORT GENERATION START ==========');
        console.log('📋 Report Type:', pendingReportType.id, '-', pendingReportType.name);

        const requiredCredits = pendingReportType.credits || 50;
        const userCredits = user?.credits || 0;

        console.log('💰 Credit check:', { 
            required: requiredCredits, 
            available: userCredits, 
            sufficient: userCredits >= requiredCredits 
        });

        if (userCredits < requiredCredits) {
            setPopupMessage(MESSAGE_TEMPLATES.INSUFFICIENT_CREDITS(requiredCredits, userCredits));
            setShowMessagePopup(true);
            setShowConfirmPopup(false);
            setPendingReportType(null);
            console.log('🚀 ========== REPORT GENERATION END (INSUFFICIENT CREDITS) ==========\n');
            return;
        }

        setShowConfirmPopup(false);
        setSelectedReportType(pendingReportType);
        setLoading(true);
        setError(null);
        setReportContent(null);
        setGenerationProgress(0);

        let progressInterval;

        try {
            // Simulate progress updates
            progressInterval = setInterval(() => {
                setGenerationProgress(prev => {
                    if (prev >= PROGRESS_CONFIG.MAX_PROGRESS) {
                        clearInterval(progressInterval);
                        return PROGRESS_CONFIG.MAX_PROGRESS;
                    }
                    return prev + PROGRESS_CONFIG.INCREMENT;
                });
            }, PROGRESS_CONFIG.INTERVAL_MS);

            // ✅ LAZY LOAD CHARTS (ONLY WHEN NEEDED)
            console.log('📊 Ensuring chart data is available...');
            const [d1Chart, d9Chart] = await Promise.all([
                ensureD1Chart(),
                ensureD9Chart()
            ]);

            console.log('📊 Charts loaded:', {
                hasD1: !!d1Chart,
                hasD9: !!d9Chart,
                d1GrahaCount: d1Chart?.graha_table?.length || 0,
                d9DataKeys: d9Chart?.data ? Object.keys(d9Chart.data).length : 0
            });

            // ✅ Build complete birth profile
            const birthProfile = buildBirthProfile();

            // ✅ Transform D9 chart for API
            const d9ChartFormatted = transformD9ChartData(d9Chart);

            if (d9ChartFormatted) {
                console.log('✅ D9 chart formatted for API:', {
                    grahaCount: d9ChartFormatted.grahatable.length,
                    chartType: d9ChartFormatted.charttype
                });
            } else {
                console.warn('⚠️ No D9 chart data formatted');
            }

            // ✅ PASS CHARTS TO BACKEND
            const requestData = {
                reportType: pendingReportType.id,
                userId: user.userId,
                userProfile: {
                    userId: user.userId,
                    fullName: user.full_name || user.name,
                    religion: user.religion || DEFAULT_VALUES.RELIGION
                },
                profile: birthProfile,
                settings: {
                    language: selectedLanguage || DEFAULT_VALUES.LANGUAGE,
                    questionType: 'REPORT',
                    detailLevel: 'comprehensive'
                },
                d1ChartData: d1Chart || null,
                d9ChartData: d9ChartFormatted || null
            };

            console.log('\n📤 ========== FINAL REQUEST DATA TO BACKEND ==========');
            console.log('📤 Request Summary:', {
                reportType: requestData.reportType,
                userId: requestData.userId,
                language: requestData.settings.language,
                hasD1Data: !!requestData.d1ChartData,
                hasD9Data: !!requestData.d9ChartData,
                d1GrahaCount: requestData.d1ChartData?.graha_table?.length || 0,
                d9GrahaCount: requestData.d9ChartData?.grahatable?.length || 0,
                profileComplete: !!birthProfile.birthdate && !!birthProfile.birthtime
            });
            console.log('📤 Full Request Data:', JSON.stringify(requestData, null, 2));
            console.log('📤 =================================================\n');

            const response = await generateReport(requestData);

            if (progressInterval) {
                clearInterval(progressInterval);
            }
            setGenerationProgress(PROGRESS_CONFIG.COMPLETE);

            console.log('\n✅ ========== BACKEND RESPONSE RECEIVED ==========');
            console.log('✅ Response:', {
                success: response.data.success,
                cached: response.data.cached,
                reportId: response.data.reportId,
                contentLength: response.data.reportContent?.length,
                spendTypeId: response.data.spendTypeId
            });
            console.log('✅ =================================================\n');

            if (response.data.success) {
                setReportContent(response.data.reportContent);
                setReportData(response.data);

                const cached = response.data.cached || false;

                if (cached) {
                    console.log('📜 Cached report loaded');
                    setPopupMessage(MESSAGE_TEMPLATES.CACHED_REPORT);
                    setShowMessagePopup(true);
                } else {
                    const spendTypeId = response.data.spendTypeId;
                    
                    if (!spendTypeId) {
                        console.warn('⚠️ No spendTypeId returned from backend');
                        setPopupMessage(MESSAGE_TEMPLATES.NO_SPEND_TYPE);
                        setShowMessagePopup(true);
                        console.log('🚀 ========== REPORT GENERATION END (NO SPEND TYPE) ==========\n');
                        return;
                    }

                    console.log('💳 New report generated, spending credits...');

                    const spendResult = await handleSpendCredits(
                        spendTypeId,
                        pendingReportType.name
                    );
                    
                    if (spendResult.success) {
                        setPopupMessage(MESSAGE_TEMPLATES.REPORT_GENERATED(
                            pendingReportType.name,
                            requiredCredits,
                            spendResult.newBalance
                        ));
                        setShowMessagePopup(true);
                        
                        console.log('✅ Credits spent, new balance:', spendResult.newBalance);
                        
                        const newReport = {
                            id: response.data.reportId,
                            report_type: pendingReportType.id,
                            report_name: pendingReportType.name,
                            profile_name: user.full_name || user.name || 'Unknown',
                            credits_used: requiredCredits,
                            created_at: response.data.generatedAt || new Date().toISOString()
                        };
                        
                        setReportHistory(prev => [newReport, ...prev]);
                        
                        console.log('✅ Report added to history manually');
                    } else {
                        console.error(' Failed to spend credits:', spendResult.error);
                        setPopupMessage(MESSAGE_TEMPLATES.CREDIT_DEDUCTION_FAILED(spendResult.error));
                        setShowMessagePopup(true);
                    }
                }

                console.log('🚀 ========== REPORT GENERATION END (SUCCESS) ==========\n');
            } else {
                throw new Error(response.data.error || 'Failed to generate report');
            }
        } catch (error) {
            console.error(' Error generating report:', error);
            console.error(' Error stack:', error.stack);
            
            if (progressInterval) {
                clearInterval(progressInterval);
            }
            setGenerationProgress(0);

            let errorMessage = 'Failed to generate report. Please try again.';
            if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }

            setError(errorMessage);
            setPopupMessage(MESSAGE_TEMPLATES.REPORT_FAILED(errorMessage));
            setShowMessagePopup(true);

            console.log('🚀 ========== REPORT GENERATION END (ERROR) ==========\n');
        } finally {
            setLoading(false);
            setPendingReportType(null);
            setTimeout(() => setGenerationProgress(0), PROGRESS_CONFIG.RESET_DELAY_MS);
        }
    }, [pendingReportType, user, selectedLanguage, handleSpendCredits, buildBirthProfile, ensureD1Chart, ensureD9Chart]);

    const handleCancelConfirmation = useCallback(() => {
        setShowConfirmPopup(false);
        setPendingReportType(null);
    }, []);

    const handleHistoryReportSelect = useCallback(async (reportId) => {
        if (!user?.userId) return;

        setLoading(true);
        setError(null);

        try {
            console.log('📜 Loading cached report from history, ID:', reportId);

            const cachedReport = reportHistory.find(r => r.id === reportId);
            
            if (!cachedReport) {
                setError('Report not found in history');
                return;
            }

            const reportType = Object.values(REPORT_TYPES).find(r => r.id === cachedReport.report_type);
            
            if (!reportType) {
                setError('Report type configuration not found');
                return;
            }

            setSelectedReportType(reportType);

            // ✅ LAZY LOAD CHARTS
            const [d1Chart, d9Chart] = await Promise.all([
                ensureD1Chart(),
                ensureD9Chart()
            ]);

            // ✅ Build complete profile
            const birthProfile = buildBirthProfile();

            // ✅ Transform D9 if available
            const d9ChartFormatted = transformD9ChartData(d9Chart);

            const response = await generateReport({
                reportType: cachedReport.report_type,
                userId: user.userId,
                userProfile: {
                    userId: user.userId,
                    fullName: user.full_name || user.name,
                    religion: user.religion || DEFAULT_VALUES.RELIGION
                },
                profile: birthProfile,
                settings: {
                    language: selectedLanguage || DEFAULT_VALUES.LANGUAGE,
                    questionType: 'REPORT',
                    detailLevel: 'comprehensive'
                },
                d1ChartData: d1Chart || null,
                d9ChartData: d9ChartFormatted || null
            });

            if (response.data.success) {
                setReportContent(response.data.reportContent);
                setReportData({
                    ...response.data,
                    cached: true
                });

                setPopupMessage(MESSAGE_TEMPLATES.CACHED_REPORT);
                setShowMessagePopup(true);

                console.log('✅ Cached report loaded successfully');
            } else {
                throw new Error(response.data.error || 'Failed to load report');
            }
        } catch (error) {
            console.error(' Error loading cached report:', error);
            setError('Failed to load report from history');
            setPopupMessage(MESSAGE_TEMPLATES.HISTORY_FAILED);
            setShowMessagePopup(true);
        } finally {
            setLoading(false);
        }
    }, [user, reportHistory, selectedLanguage, buildBirthProfile, ensureD1Chart, ensureD9Chart]);

    const handleNewReport = useCallback(() => {
        setReportContent(null);
        setReportData(null);
        setSelectedReportType(null);
        setError(null);
        setGenerationProgress(0);
    }, []);

    // ═══════════════════════════════════════════════════════════════════════════════
    // ✅ RENDER
    // ═══════════════════════════════════════════════════════════════════════════════
    
    if (!user) {
        return (
            <div className="reports-loading">
                <div className="loading-spinner"></div>
                <p>Loading user data...</p>
            </div>
        );
    }

    const reportTypesArray = Object.values(REPORT_TYPES);

    return (
        <div className="about-container">
            <div className="heading">
                <h3>Astrology Reports</h3>
                {/* <h6>Generate comprehensive astrology reports based on your birth chart</h6> */}
            </div>

            {reportContent && (
                <ReportViewer
                    reportContent={reportContent}
                    selectedReportType={selectedReportType}
                    reportData={reportData}
                    onNewReport={handleNewReport}
                    userName={user.full_name || user.name}
                    userProfile={user}
                    preferredLanguage={selectedLanguage}
                />
            )}

            {loading && (
                <LoadingIndicator
                    reportName={selectedReportType?.name}
                    progress={generationProgress}
                    color={selectedReportType?.color}
                />
            )}

            {error && !loading && (
                <ErrorDisplay error={error} onRetry={handleNewReport} />
            )}

            {!reportContent && !loading && !error && (
                <>
                    <ReportTypeList 
                        reportTypes={reportTypesArray}
                        onReportSelect={handleReportTypeClick}
                    />

                    {reportHistory.length > 0 && (
                        <ReportHistory 
                            reportHistory={reportHistory}
                            onReportSelect={handleHistoryReportSelect}
                        />
                    )}
                </>
            )}

            {showConfirmPopup && pendingReportType && (
                <ConfirmationPopup
                    reportType={pendingReportType}
                    userCredits={user?.credits || 0}
                    onConfirm={handleConfirmGeneration}
                    onCancel={handleCancelConfirmation}
                />
            )}

            <MessagePopup
                show={showMessagePopup}
                message={popupMessage}
                setShow={setShowMessagePopup}
            />
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ✅ SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

const LoadingIndicator = ({ reportName, progress, color }) => (
    <div className="consultation-types">
    <div className="loading-spinner"></div>
    <div className="booking-info">
        <h3 className="heading">
            Generating Your {reportName}...
        </h3>
    </div>
    <p className="waiting-description">
        This may take 30-60 seconds. Our AI is analyzing your birth chart in detail.
    </p>
    
    <div className="progress-bar-container">
        <div 
            className="progress-bar-fill" 
            style={{ 
                width: `${progress}%`, 
                backgroundColor: color || '#4CAF50' 
            }}
        ></div>
    </div>
    <p className="progress-text">{progress}% Complete</p>
</div>
);

const ErrorDisplay = ({ error, onRetry }) => (
    <div className="waiting-description">
        <p>{error}</p>
        <button onClick={onRetry} className="book-call-btn">
            Try Again
        </button>
    </div>
);

const ConfirmationPopup = ({ reportType, userCredits, onConfirm, onCancel }) => {
    const hasEnoughCredits = userCredits >= reportType.credits;

    return (
        <div className="popup-overlay">
            <div className="popup-content confirmation-popup">
                <button className="popup-close" onClick={onCancel}>×</button>

                <div className="popup-icon" style={{ backgroundColor: reportType.color }}>
                    ✨
                </div>

                <h2>{reportType.name}</h2>
                <p className="popup-description">{reportType.description}</p>

                <div className="popup-credit-info">
                    <div className="credit-required">
                        <span className="credit-label">Credits Required</span>
                        <span className="credit-value">{reportType.credits}</span>
                    </div>
                    <div className="credit-available">
                        <span className="credit-label">Your Credits</span>
                        <span className={`credit-value ${hasEnoughCredits ? 'sufficient' : 'insufficient'}`}>
                            {userCredits}
                        </span>
                    </div>
                </div>

                {!hasEnoughCredits && (
                    <div className="insufficient-credits-warning">
                        ⚠️ You don't have enough credits to generate this report
                    </div>
                )}

                <div className="popup-actions">
                    <button 
                        className="btn-confirm" 
                        onClick={onConfirm}
                        disabled={!hasEnoughCredits}
                    >
                        Generate Report
                    </button>
                    <button className="btn-cancel" onClick={onCancel}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReportsPage;
